import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppConfig } from '../models/app-config';

/**
 * Service for loading and accessing runtime configuration from assets/config.json.
 * This allows using a single build artifact with different configurations per deployment.
 */
@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private config: AppConfig | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Loads the runtime configuration from assets/config.json.
   * This method should be called during application initialization (APP_INITIALIZER).
   * 
   * @returns Promise that resolves when configuration is loaded
   * @throws Error if configuration file cannot be loaded
   */
  load(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.http.get<AppConfig>('assets/config.json')
        .subscribe({
          next: (config) => {
            // Apply defaults for optional fields
            this.config = {
              ...config,
              clarityProjectId: config.clarityProjectId ?? null,
              msalClientId: config.msalClientId ?? undefined,
              msalAuthority: config.msalAuthority ?? undefined,
              msalScopes: config.msalScopes ?? undefined
            };
            
            console.log('Runtime configuration loaded successfully:', this.config);
            resolve();
          },
          error: (error) => {
            console.error('Failed to load runtime configuration from assets/config.json:', error);
            reject(error);
          }
        });
    });
  }

  /**
   * Gets a specific configuration value by key.
   * 
   * @param key - The configuration key to retrieve
   * @returns The configuration value for the specified key
   * @throws Error if configuration has not been loaded yet
   */
  get<T extends keyof AppConfig>(key: T): AppConfig[T] {
    if (!this.config) {
      throw new Error('Configuration not loaded. Ensure ConfigService.load() is called during APP_INITIALIZER.');
    }
    return this.config[key];
  }

  /**
   * Gets the entire configuration object.
   * 
   * @returns The complete AppConfig object
   * @throws Error if configuration has not been loaded yet
   */
  getAll(): AppConfig {
    if (!this.config) {
      throw new Error('Configuration not loaded. Ensure ConfigService.load() is called during APP_INITIALIZER.');
    }
    return this.config;
  }

  /**
   * Checks if the configuration has been loaded.
   * 
   * @returns true if configuration is loaded, false otherwise
   */
  isLoaded(): boolean {
    return this.config !== null;
  }
}
